import React, { useState } from 'react';

import { type BudgetTemplateLine } from 'loot-core/shared/budget-template';
import {
  type CategoryEntity,
  type CategoryGroupEntity,
} from 'loot-core/types/models';

import { theme } from '../../../style';
import { Link } from '../../common/Link';
import { Modal } from '../../common/Modal';
import { View } from '../../common/View';
import { type ITableTotals } from '../types';

import { CashFlowProjectionChart } from './CashFlowProjectionChart';
import { Datasource } from './shared';
import { SpendingDistributionChart } from './SpendingDistributionChart';

type IBudgetTemplateChartModalProps = {
  year: number;
  budgetLines: BudgetTemplateLine[];
  categoryGroups: CategoryGroupEntity[];
  categories: CategoryEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  datasource: Datasource;
  setShowModal: (show: boolean) => void;
};

export function ChartModal({
  year,
  categoryGroups,
  categories,
  groupTotals,
  budgetLines,
  datasource,
  setShowModal,
}: IBudgetTemplateChartModalProps) {
  return (
    <Modal
      isCurrent={true}
      showClose={true}
      stackIndex={0}
      title={
        datasource === Datasource.BudgetTemplate
          ? 'Budget Template Projection'
          : 'Annual Budget Summary'
      }
      onClose={() => {
        setShowModal(false);
      }}
    >
      <View
        style={{
          width: Math.min(window.screen.width - 100, 800),
          flexDirection: window.screen.width > 800 ? 'row' : 'column',
          gap: 15,
          flex: 1,
          justifyContent: 'space-between',
        }}
      >
        <View style={{ flex: 1, borderRight: `1px solid ${theme.cardBorder}` }}>
          <SpendingDistributionChart
            year={year}
            budgetLines={budgetLines}
            categoryGroups={categoryGroups}
            categories={categories}
            groupTotals={groupTotals}
            datasource={datasource}
          />
        </View>
        <View style={{ flex: 1, justifyContent: 'space-between' }}>
          <CashFlowProjectionChart
            year={year}
            budgetLines={budgetLines}
            categoryGroups={categoryGroups}
            groupTotals={groupTotals}
            datasource={datasource}
          />
        </View>
      </View>
    </Modal>
  );
}

type IBudgetTemplateChartModalButtonProps = {
  year: number;
  categoryGroups: CategoryGroupEntity[];
  categories: CategoryEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  budgetLines: BudgetTemplateLine[];
  datasource: Datasource;
};

function ChartModalButton({
  year,
  budgetLines,
  categoryGroups,
  categories,
  groupTotals,
  datasource,
}: IBudgetTemplateChartModalButtonProps) {
  const [showModal, setShowModal] = useState<boolean>(false);

  return (
    <>
      <Link
        variant={'text'}
        style={{ color: theme.pageTextLink }}
        data-adoption-id={
          datasource === Datasource.BudgetTemplate
            ? `planning-budget-template-btn`
            : `planning-annual-template-btn`
        }
        onClick={() => {
          setShowModal(true);
        }}
      >
        {datasource === Datasource.BudgetTemplate
          ? `Budget template projection`
          : `Annual budget summary`}
      </Link>

      {showModal && (
        <ChartModal
          year={year}
          categoryGroups={categoryGroups}
          categories={categories}
          groupTotals={groupTotals}
          budgetLines={budgetLines}
          datasource={datasource}
          setShowModal={setShowModal}
        />
      )}
    </>
  );
}

export default ChartModalButton;
